import React, { useState, useEffect, useCallback } from 'react';
import { getCertificates, getCertificateById, approveCertificate } from '../api';
import Pagination from './Pagination';
import logger from '../logger';
import './CertificateReview.css';

const CertificateReview = () => {
    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const perPage = 25;

    // Filters
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Detail modal
    const [selectedCertificate, setSelectedCertificate] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Action modal
    const [actionModal, setActionModal] = useState({ open: false, type: '', certificateId: null });
    const [actionReason, setActionReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const fetchCertificates = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getCertificates(statusFilter, currentPage, perPage);
            setCertificates(result.data);
            setTotalPages(result.meta.totalPages);
            setTotal(result.meta.total);
        } catch (err) {
            logger.error('Failed to fetch certificates:', err);
            setError('Failed to load certificates. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, currentPage]);

    useEffect(() => {
        fetchCertificates();
    }, [fetchCertificates]);

    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter]);

    const handleViewDetails = async (certificateId) => {
        setDetailLoading(true);
        try {
            const detail = await getCertificateById(certificateId);
            setSelectedCertificate(detail);
        } catch (err) {
            logger.error('Failed to fetch certificate details:', err);
            setError('Failed to load certificate details.');
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        setSelectedCertificate(null);
    };

    const openActionModal = (type, certificateId) => {
        setActionModal({ open: true, type, certificateId });
        setActionReason('');
    };

    const closeActionModal = () => {
        setActionModal({ open: false, type: '', certificateId: null });
        setActionReason('');
    };

    const handleAction = async () => {
        if (!actionModal.certificateId) return;

        setActionLoading(true);
        try {
            await approveCertificate(
                actionModal.certificateId,
                actionModal.type === 'approve',
                actionReason
            );
            setSuccessMessage(`Certificate ${actionModal.type === 'approve' ? 'approved' : 'rejected'} successfully.`);
            setTimeout(() => setSuccessMessage(''), 3000);
            closeActionModal();
            closeDetail();
            fetchCertificates();
        } catch (err) {
            logger.error(`Failed to ${actionModal.type} certificate:`, err);
            setError(`Failed to ${actionModal.type} certificate. Please try again.`);
        } finally {
            setActionLoading(false);
        }
    };

    const filteredCertificates = certificates.filter(cert => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        const learnerName = (cert.learner?.full_name || cert.learner_name || '').toLowerCase();
        const courseName = (cert.course?.name || cert.course_name || '').toLowerCase();
        return learnerName.includes(search) || courseName.includes(search);
    });

    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved': return 'status-approved';
            case 'pending': return 'status-pending';
            case 'rejected': return 'status-rejected';
            default: return 'status-unknown';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    return (
        <div className="certificate-review-page">
            <div className="certificate-header">
                <div className="header-content">
                    <h1>Certificate Review</h1>
                    <p>Review and approve learner certificates</p>
                </div>
                <div className="header-stats">
                    <div className="stat-card">
                        <span className="stat-number">{total}</span>
                        <span className="stat-label">Total Certificates</span>
                    </div>
                </div>
            </div>

            {error && (
                <div className="error-banner">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                    </svg>
                    {error}
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {successMessage && (
                <div className="success-banner">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                    {successMessage}
                </div>
            )}

            <div className="certificate-toolbar">
                <div className="filter-tabs">
                    {['all', 'pending', 'approved', 'rejected'].map(status => (
                        <button
                            key={status}
                            className={`filter-tab ${statusFilter === status ? 'active' : ''}`}
                            onClick={() => setStatusFilter(status)}
                        >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                    ))}
                </div>

                <div className="search-box">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search by learner or course..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="certificate-content">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading certificates...</p>
                    </div>
                ) : filteredCertificates.length === 0 ? (
                    <div className="empty-state">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="12" y1="18" x2="12" y2="12" />
                            <line x1="9" y1="15" x2="15" y2="15" />
                        </svg>
                        <h3>No certificates found</h3>
                        <p>
                            {searchTerm
                                ? 'Try adjusting your search terms.'
                                : 'No certificates match the selected filter.'}
                        </p>
                    </div>
                ) : (
                    <>
                        <table className="certificates-table">
                            <thead>
                                <tr>
                                    <th>Learner</th>
                                    <th>Course</th>
                                    <th>Completion Date</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCertificates.map(cert => (
                                    <tr key={cert.id}>
                                        <td>
                                            <div className="learner-info">
                                                <div className="learner-avatar">
                                                    {(cert.learner?.full_name || cert.learner_name || 'U')[0].toUpperCase()}
                                                </div>
                                                <span>{cert.learner?.full_name || cert.learner_name || 'Unknown'}</span>
                                            </div>
                                        </td>
                                        <td>{cert.course?.name || cert.course_name || 'Unknown Course'}</td>
                                        <td>{formatDate(cert.completion_date || cert.created_at)}</td>
                                        <td>
                                            <span className={`status-badge ${getStatusBadgeClass(cert.status)}`}>
                                                {cert.status || 'Unknown'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    className="btn-view"
                                                    onClick={() => handleViewDetails(cert.id)}
                                                >
                                                    View
                                                </button>
                                                {cert.status?.toLowerCase() === 'pending' && (
                                                    <>
                                                        <button
                                                            className="btn-approve"
                                                            onClick={() => openActionModal('approve', cert.id)}
                                                        >
                                                            Approve
                                                        </button>
                                                        <button
                                                            className="btn-reject"
                                                            onClick={() => openActionModal('reject', cert.id)}
                                                        >
                                                            Reject
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            isLoading={loading}
                        />
                    </>
                )}
            </div>

            {/* Detail Modal */}
            {selectedCertificate && (
                <div className="modal-overlay" onClick={closeDetail}>
                    <div className="certificate-detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Certificate Details</h2>
                            <button className="close-btn" onClick={closeDetail}>×</button>
                        </div>

                        {detailLoading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                            </div>
                        ) : (
                            <div className="modal-body">
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Learner Name</label>
                                        <span>{selectedCertificate.learner?.full_name || selectedCertificate.learner_name || 'N/A'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Course</label>
                                        <span>{selectedCertificate.course?.name || selectedCertificate.course_name || 'N/A'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Completion Date</label>
                                        <span>{formatDate(selectedCertificate.completion_date || selectedCertificate.created_at)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Score</label>
                                        <span>{selectedCertificate.score || 'N/A'}%</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Status</label>
                                        <span className={`status-badge ${getStatusBadgeClass(selectedCertificate.status)}`}>
                                            {selectedCertificate.status || 'Unknown'}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Certificate ID</label>
                                        <span>{selectedCertificate.certificate_number || selectedCertificate.id}</span>
                                    </div>
                                </div>

                                {selectedCertificate.preview_url && (
                                    <div className="certificate-preview">
                                        <label>Certificate Preview</label>
                                        <img src={selectedCertificate.preview_url} alt="Certificate Preview" />
                                    </div>
                                )}

                                {selectedCertificate.status?.toLowerCase() === 'pending' && (
                                    <div className="modal-actions">
                                        <button
                                            className="btn-approve"
                                            onClick={() => openActionModal('approve', selectedCertificate.id)}
                                        >
                                            Approve Certificate
                                        </button>
                                        <button
                                            className="btn-reject"
                                            onClick={() => openActionModal('reject', selectedCertificate.id)}
                                        >
                                            Reject Certificate
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Action Modal (Approve/Reject) */}
            {actionModal.open && (
                <div className="modal-overlay" onClick={closeActionModal}>
                    <div className="action-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{actionModal.type === 'approve' ? 'Approve' : 'Reject'} Certificate</h2>
                            <button className="close-btn" onClick={closeActionModal}>×</button>
                        </div>
                        <div className="modal-body">
                            <p>
                                Are you sure you want to {actionModal.type} this certificate?
                            </p>
                            {actionModal.type === 'reject' && (
                                <div className="form-group">
                                    <label>Reason for rejection (optional)</label>
                                    <textarea
                                        value={actionReason}
                                        onChange={e => setActionReason(e.target.value)}
                                        placeholder="Enter reason for rejection..."
                                        rows={3}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={closeActionModal}>
                                Cancel
                            </button>
                            <button
                                className={actionModal.type === 'approve' ? 'btn-approve' : 'btn-reject'}
                                onClick={handleAction}
                                disabled={actionLoading}
                            >
                                {actionLoading ? 'Processing...' : (actionModal.type === 'approve' ? 'Approve' : 'Reject')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CertificateReview;
